import type { JiraCustomField, JiraCustomFieldsResponse } from '@/types';
import { useQuery } from '@/utils/api';

import { useAxios } from './useAxios';

export interface UseJiraOptionalCustomFieldsOptions {
  accountKey?: string;
  projectKey?: string;
  issueType?: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
}

export interface UseJiraOptionalCustomFieldsResult {
  fields: JiraCustomField[];
}

/**
 * Fetches the *optional custom fields* on the Jira create screen for the
 * given (projectKey, issueType). These are non-required fields that may be
 * enforced by workflow validators during transitions. Used in the Transitions
 * tab so clients can configure default values applied before transitions.
 */
export const useJiraOptionalCustomFields = ({
  accountKey,
  projectKey,
  issueType,
  config,
  enabled = true,
}: UseJiraOptionalCustomFieldsOptions) => {
  const axios = useAxios();

  return useQuery({
    defaultErrorMessage: 'Failed to fetch Jira optional custom fields',
    queryKey: [
      'jira',
      'optional-custom-fields',
      projectKey,
      issueType,
      accountKey ?? 'new',
    ],
    queryFn: async (): Promise<UseJiraOptionalCustomFieldsResult> => {
      const response = await axios.post<JiraCustomFieldsResponse>(
        '/integration/jira/optional-custom-fields',
        {
          provider: 'jira',
          account: accountKey,
          secret: accountKey ? undefined : config,
          projectKey,
          issueType,
        }
      );

      return { fields: response.data?.fields ?? [] };
    },
    enabled:
      enabled && !!projectKey && !!issueType && (!!accountKey || !!config),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
